import React, { memo } from "react";
import { DLS_TEXT_VARIANTS } from "../../../constants/dls";
import { LeftGradientLine, ReferEarnContainer, ReferEarnText, RightGradientLine } from "../../../styles/home";
import {
    ReferralsContentContainer,
    ReferralItem,
    ReferralImage,
    ReferralInfo,
    ReferralName,
    ReferralDivider,
    ViewAllTextButton,
    ChevronIconContainer,
} from "../../../styles/referrals";
import { Images, RemoteImages } from "../../../constants/images";
import { IReferral, ReferralState } from "../../../interfaces/home";
import { GenericIcon } from "@swiggy-private/dls-web-icons/dist/GenericIconWeb";
import { Svg12 as ChevronRight } from "@swiggy-private/dls-web-icons/dist/web/generic/chevronRight";
import { isValidData } from "utils/object";
import router from "next/router";
import { Tag } from "components/Tag";
import { ReferralExpirationText } from "components/Referrals/ReferralExpirationText";
import { logViewAllReferralsClick } from "../analytics";
import { logReferralCardClick } from "../../Referrals/analytics";
import { getImageKitUrl } from "utils/cdn";
import { Image } from "@swiggy-private/dls-web-core/dist/components/Image";

interface ReferralsSuccessViewProps {
    referrals: IReferral[];
}

const ReferralItemComponent = ({ referral }: { referral: IReferral }) => {
    const handleReferralClick = () => {
        logReferralCardClick({
            referral_id: referral?.referral_id || "",
            referee_name: referral?.referee?.name || "",
            referee_status: referral?.referral_state || "",
        });
        router.push(`/referrals?id=${referral?.referral_id?.replace("#", "_")}`);
    };

    // Map API status to display status
    const getDisplayStatus = (status: string) => {
        switch (status) {
            case "PENDING":
                return "Pending";
            case "IN_PROGRESS":
                return "In Progress";
            case "EARNED":
                return "Earned";
            case "EXPIRED":
                return "Expired";
            case "PAYMENT_PENDING":
                return "Processing";
            default:
                return status;
        }
    };

    const displayStatus = getDisplayStatus(referral.referral_state);

    const getStatusVariant = (status: string) => {
        switch (status) {
            case "PENDING":
                return "warning";
            case "IN_PROGRESS":
                return "primary";
            case "EARNED":
                return "success";
            case "EXPIRED":
                return "expired";
            case "PAYMENT_PENDING":
                return "warning";
            default:
                return "secondary";
        }
    };

    const getStatusText = () => {
        if (referral.referral_state === ReferralState.EARNED && referral.bonus_earned) {
            return `Earned ${referral.bonus_earned}`;
        }
        return displayStatus;
    };

    return (
        <>
            <ReferralItem key={referral?.referral_id} onClick={handleReferralClick}>
                <Image
                    width="48px"
                    height="48px"
                    fit="cover"
                    src={
                        referral?.referee?.image_url ||
                        getImageKitUrl(RemoteImages.defaultAvatar, { width: 80, height: 80 })
                    }
                />
                <ReferralInfo>
                    <ReferralName variant={DLS_TEXT_VARIANTS.BODY_MEDIUM_NEUTRAL}>
                        {referral?.referee?.name}
                    </ReferralName>
                    {!!referral.referral_expire_at &&
                        referral.referral_state === ReferralState.PENDING &&
                        !referral.referee?.ozone_id && (
                            <ReferralExpirationText expirationTimestamp={referral.referral_expire_at} />
                        )}

                    <Tag variant={getStatusVariant(referral.referral_state)} size="small">
                        {getStatusText()}
                    </Tag>
                </ReferralInfo>
                <ChevronIconContainer>
                    <GenericIcon Icon={ChevronRight} fillColor="text_Med_Emphasis" />
                </ChevronIconContainer>
            </ReferralItem>
            <ReferralDivider />
        </>
    );
};

const ReferralsSuccessView = ({ referrals }: ReferralsSuccessViewProps) => {
    const handleViewAll = () => {
        logViewAllReferralsClick();
        router.push("/referrals");
    };

    if (!isValidData(referrals) || referrals?.length === 0) {
        return null;
    }

    return (
        <>
            <ReferEarnContainer>
                <LeftGradientLine />
                <ReferEarnText variant={DLS_TEXT_VARIANTS.OVERLINE_NEUTRAL_BOLD_SPACED}>YOUR REFERRALS</ReferEarnText>
                <RightGradientLine />
            </ReferEarnContainer>
            <ReferralsContentContainer>
                {referrals?.map((referral) => {
                    return <ReferralItemComponent key={referral?.referral_id} referral={referral} />;
                })}
                <ViewAllTextButton color="primary" buttonSize="medium" removePadding onClick={handleViewAll}>
                    View All
                </ViewAllTextButton>
            </ReferralsContentContainer>
        </>
    );
};

export default memo(ReferralsSuccessView);
